// src/components/SearchBar.js
import { useState, useEffect, useRef, memo } from "react";
import {
  TextField,
  IconButton,
  List,
  ListItem,
  ListItemAvatar,
  Avatar,
  ListItemText,
  Button,
  Snackbar,
  Alert,
  Box,
  Typography,
  CircularProgress,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import LibraryMusicIcon from "@mui/icons-material/LibraryMusic";
import { searchYouTube, addToQueue, getFrequentSongs } from "../api";
import { decode } from 'html-entities';

const decodeHtmlEntities = (text) => (text ? decode(text) : text);

function SearchBar({ room, onQueueChange }) {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searching, setSearching] = useState(false);
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [showFrequent, setShowFrequent] = useState(false);
  const [frequentSongs, setFrequentSongs] = useState([]);
  const [loadingFrequent, setLoadingFrequent] = useState(false);

  const searchInputRef = useRef(null);
  const searchTimeoutRef = useRef(null);
  const isSearchingRef = useRef(false);   // ← New ref to control suggestions
  const frequentListRef = useRef(null);

  const suggestion_delay = 3000; // ms

  // Main Search
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (frequentListRef.current && !frequentListRef.current.contains(event.target) && !event.target.closest('#frequent-songs-btn')) {
        setShowFrequent(false);
      }
    };

    if (showFrequent) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showFrequent]);

  const handleSearch = async () => {
    if (!query.trim()) return;

    isSearchingRef.current = true;        // Block suggestions
    setSearching(true);
    setShowSuggestions(false);
    setSuggestions([]);

    try {
      const res = await searchYouTube(query);
      setResults(res);
      setQuery("");
    } catch (error) {
      console.error("Search error:", error);
      setResults([]);
    } finally {
      setSearching(false);
      // Keep suggestions blocked for a short moment after search
      setTimeout(() => {
        isSearchingRef.current = false;
      }, suggestion_delay + 500);
    }

    // Force blur
    if (searchInputRef.current) {
      searchInputRef.current.blur();
    }
  };

  const handleAdd = async (video) => {
    const storedUserName = sessionStorage.getItem("userName") || "anonymous";
    await addToQueue({
      videoId: video.videoId,
      title: video.title,
      channel: video.channel,
      addedBy: storedUserName,
      room,
    });
    setSnackbarMessage(`Added "${decodeHtmlEntities(video.title)}" to queue`);
    setOpenSnackbar(true);
  };

  const loadFrequentSongs = async () => {
    setLoadingFrequent(true);
    try {
      const songs = await getFrequentSongs();
      setFrequentSongs(songs);
      // if (songs.length > 0) {
      //   setShowFrequent(true);
      // }
    } catch (err) {
      console.error("Failed to load frequent songs:", err);
    } finally {
      setLoadingFrequent(false);
    }
  };

  const handleFrequentSongsClick = () => {
    setShowFrequent(prev => {
      if (prev) {
        return false;
      }
      else {
        loadFrequentSongs();
        return true;
      }
    });
  };

  const handleCloseSnackbar = () => setOpenSnackbar(false);

  // Real-time suggestions while typing
  useEffect(() => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    // Don't show suggestions if we're in the middle of a full search
    if (isSearchingRef.current) return;

    const queryLength = query.trim().length;
    if (queryLength < 2) {
      setSuggestions([]);
      setShowSuggestions(false);
      return;
    }

    searchTimeoutRef.current = setTimeout(async () => {
      if (!query.trim() || isSearchingRef.current) return;

      setLoading(true);
      try {
        const res = await searchYouTube(query);
        setSuggestions(res.slice(0, 10));
        setShowSuggestions(true);
      } catch (error) {
        console.error("Suggestion error:", error);
        setSuggestions([]);
        setShowSuggestions(false);
      } finally {
        setLoading(false);
      }
    }, suggestion_delay);

    return () => {
      if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    };
  }, [query]);

  const handleSuggestionClick = (suggestion) => {
    setQuery(suggestion.title);
    setSuggestions([]);
    setShowSuggestions(false);
    if (searchInputRef.current) searchInputRef.current.blur();
  };

  return (
    <>
      <Box sx={{ display: "flex", gap: 1, alignItems: "flex-start" }}>
        <IconButton
          id="frequent-songs-btn"
          onClick={handleFrequentSongsClick}
          sx={{
            bgcolor: showFrequent ? "action.selected" : "rgba(0,0,0,0.08)",
            "&:hover": { bgcolor: "action.selected" },
            minWidth: 48,
          }}
        >
          <LibraryMusicIcon />
        </IconButton>
        <TextField
          fullWidth
          placeholder="Search YouTube song..."
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              handleSearch();
            }
          }}
          InputProps={{
            endAdornment: (
              <IconButton onClick={handleSearch} disabled={searching || loading}>
                {searching ? <CircularProgress size={24} /> : <SearchIcon />}
              </IconButton>
            ),
          }}
          inputRef={searchInputRef}
          sx={{ backgroundColor: 'white', borderRadius: 1 }}
        />
      </Box>

      {/* Suggestions dropdown - only show when not searching */}
      {showSuggestions && suggestions.length > 0 && !searching && (
        <Box sx={{
          mt: 1,
          maxHeight: 300,
          overflow: "auto",
          border: "1px solid #ccc",
          borderRadius: 1,
          bgcolor: "background.paper",
          position: "relative",
          zIndex: 1000
        }}>
          <Typography variant="caption" sx={{ px: 2, py: 1, bgcolor: "grey.100", fontWeight: "medium" }}>
            Suggestions
          </Typography>
          <List>
            {suggestions.map((video) => (
              <ListItem
                key={video.videoId}
                sx={{ cursor: "pointer" }}
                onClick={() => handleSuggestionClick(video)}
              >
                <ListItemAvatar>
                  <Avatar src={video.thumb} variant="square" />
                </ListItemAvatar>
                <ListItemText
                  primary={decodeHtmlEntities(video.title)}
                  secondary={video.channel}
                />
              </ListItem>
            ))}
          </List>
        </Box>
      )}

      {/* Frequent Songs Dropdown */}
      {showFrequent && (
        <Box ref={frequentListRef} sx={{
          mt: 1,
          maxHeight: 300,
          overflow: "auto",
          border: "1px solid #ccc",
          borderRadius: 1,
          bgcolor: "background.paper",
          position: "relative",
          zIndex: 1000
        }}>
          <Box sx={{ px: 2, py: 1, bgcolor: "grey.100", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <Typography variant="caption" sx={{ fontWeight: "medium" }}>
              Frequently Added Songs
            </Typography>
            <Typography variant="caption" sx={{ color: "text.secondary" }}>
              {frequentSongs.length} songs
            </Typography>
          </Box>
          {loadingFrequent ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
              <CircularProgress size={24} />
            </Box>
          ) : frequentSongs.length === 0 ? (
            <Typography variant="caption" sx={{ display: "block", px: 2, py: 2, color: "text.secondary" }}>
              No frequently added songs. Add songs to see them here.
            </Typography>
          ) : (
            <List>
              {frequentSongs.map((video) => (
                <ListItem
                  key={video.videoId}
                  divider
                  secondaryAction={
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={() => {
                        handleAdd(video);
                        loadFrequentSongs();
                      }}
                    >
                      Add
                    </Button>
                  }
                >
                  <ListItemAvatar>
                    <Avatar src={video.thumb} variant="square" sx={{ width: 56, height: 56 }} />
                  </ListItemAvatar>
                  <ListItemText
                    primary={decodeHtmlEntities(video.title)}
                    secondary={
                      <>
                        {video.channel} &middot; Added {video.count} time{video.count !== 1 ? "s" : ""}
                      </>
                    }
                  />
                </ListItem>
              ))}
            </List>
          )}
        </Box>
      )}

      {/* Main Search Results */}
      <Box sx={{ mt: 2, minHeight: 100 }}>
        {searching ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
            <CircularProgress />
            <Typography sx={{ ml: 2, mt: 0.5 }}>Searching YouTube...</Typography>
          </Box>
        ) : (
          <List>
            {results.map((video) => (
              <ListItem 
                key={video.videoId} 
                divider
                secondaryAction={
                  <Button variant="contained" size="small" onClick={() => handleAdd(video)}>
                    Add
                  </Button>
                }
              >
                <ListItemAvatar>
                  <Avatar src={video.thumb} variant="square" />
                </ListItemAvatar>
                <ListItemText
                  primary={decodeHtmlEntities(video.title)}
                  secondary={video.channel}
                />
              </ListItem>
            ))}
          </List>
        )}
      </Box>

      <Snackbar
        open={openSnackbar}
        autoHideDuration={3000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseSnackbar} severity="success">
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </>
  );
}

export default memo(SearchBar);